import { useState } from "react";
import { Dialog, Text, useToast } from "@flat-ui";
import { AdmPlan } from "@custom-types/base-types";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useAppDispatch } from "@store/store-helper";
import { unassignSceneSeat } from "@store/plans/plans-thunks";
import { getPlanAssigneeName } from "@utils/data-display";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";

interface Props {
  /** The SCENE seat that should become free again */
  plan: AdmPlan;

  /** Function called when the user closes the dialog */
  onClose(): void;
}

/**
 * Dialog to free the SCENE seat of a workspace member again, so that it can be given to
 * somebody else. The member keeps their workspace membership, they only lose the access to SCENE.
 */
export function UnassignSceneSeatDialog({
  plan,
  onClose,
}: Props): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandler();
  const [isUnassigning, setIsUnassigning] = useState<boolean>(false);

  const assigneeName = getPlanAssigneeName(plan);

  async function handleConfirm(): Promise<void> {
    trackEvent({
      name: PlanEvents.unassignSceneSeat,
      props: {
        planId: plan.id,
      },
    });

    try {
      setIsUnassigning(true);

      await dispatch(unassignSceneSeat({ plan })).unwrap();

      openToast({
        title: "The SCENE seat is available again!",
      });
      onClose();
    } catch (error) {
      handleErrorWithToast({
        id: `unassign-scene-seat-${Date.now().toString()}`,
        title: "Could not unassign the SCENE seat",
        error,
      });
    } finally {
      setIsUnassigning(false);
    }
  }

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      title="Unassign this SCENE seat"
      onConfirm={handleConfirm}
      showSpinner={isUnassigning}
      confirmText="Yes, unassign"
      variant="danger"
    >
      <Text variant="bodyL">
        The SCENE seat of <strong>{assigneeName}</strong> becomes available
        again and the member loses the access to SCENE. They stay a member of
        the workspace.
      </Text>
    </Dialog>
  );
}
